import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import supabaseClient from '../supabaseClient';
import { Box, Container, Grid, Typography, Divider, Card, List, ListItem, ListItemIcon, ListItemText, Chip, Pagination, CircularProgress, Avatar, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Collapse } from '@mui/material';
import { Add as AddIcon, ArrowUpward, ArrowDownward, Comment, Visibility, ChatBubbleOutline, Send } from '@mui/icons-material';
import Nav1 from '../components/Nav1';
import Footer from '../components/Footer';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from 'react-router-dom';

const Question = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [questionsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [openAskDialog, setOpenAskDialog] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    title: '',
    content: '',
    tags: ''
  });
  const [questions, setQuestions] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openComments, setOpenComments] = useState({});
  const [newComment, setNewComment] = useState({});
  const [comments, setComments] = useState({});

  useEffect(() => {
    console.log('Component mounted, fetching questions...');
    fetchQuestions();
    
    const subscription = supabaseClient
      .channel('questions2')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'questions2' 
        }, 
        (payload) => {
          console.log('Real-time update received:', payload);
          fetchQuestions();
      })
      .subscribe();

    return () => {
      console.log('Cleaning up subscription');
      subscription.unsubscribe();
    };
  }, []);

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      console.log('Fetching questions...');
      // First, let's try a simple select without joins to verify basic data access
      const { data, error } = await supabaseClient
        .from('questions2')
        .select('*');

      console.log('Raw Supabase response:', { data, error });

      if (error) {
        console.error('Error details:', error);
        throw error;
      }
      
      if (data) {
        console.log('Setting questions:', data);
        setQuestions(data);
      } else {
        console.log('No data returned from Supabase');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add this console log to check if questions state is updating
  useEffect(() => {
    console.log('Questions state updated:', questions);
  }, [questions]);

  // Get current questions
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = questions
    .slice(indexOfFirstQuestion, indexOfLastQuestion);
  const totalPages = Math.ceil(questions.length / questionsPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleVote = async (questionId, direction) => {
    if (!user) return; // Ensure user is logged in

    try {
      const voteValue = direction === 'up' ? 1 : -1;
      const { data, error } = await supabaseClient
        .from('votes')
        .upsert({
          question_id: questionId,
          user_id: user.id,
          value: voteValue
        }, {
          onConflict: 'question_id,user_id'
        });

      if (error) throw error;
      fetchQuestions(); // Refresh questions to show updated votes
    } catch (error) {
      console.error('Error voting:', error);
    }
  };

  const handleOpenAskDialog = () => {
    setOpenAskDialog(true);
  };

  const handleCloseAskDialog = () => {
    setOpenAskDialog(false);
    setNewQuestion({ title: '', content: '', tags: '' });
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    if (!user) return; // Ensure user is logged in

    try {
      const questionData = {
        title: newQuestion.title,
        content: newQuestion.content,
        tags: newQuestion.tags.split(',').map(tag => tag.trim()),
        user_id: user.id,
        votes: 0,
        answers: 0,
        views: 0
      };

      console.log('Submitting question:', questionData);

      const { data, error } = await supabaseClient
        .from('questions2')
        .insert([questionData]);

      if (error) {
        console.error('Error details:', error);
        throw error;
      }

      console.log('Question submitted successfully:', data);
      handleCloseAskDialog();
      fetchQuestions(); // Refresh questions to show the new one
    } catch (error) {
      console.error('Error submitting question:', error);
    }
  };

  const handleViewQuestion = async (questionId) => {
    try {
      console.log('Handling view for question ID:', questionId);
      
      // First get the current question data
      const { data: currentQuestion, error: fetchError } = await supabaseClient
        .from('questions2')
        .select('views')
        .eq('id', questionId)
        .single();

      if (fetchError) {
        console.error('Error fetching question:', fetchError);
        throw fetchError;
      }

      console.log('Current question data:', currentQuestion);

      // Update view count
      const { error: updateError } = await supabaseClient
        .from('questions2')
        .update({ views: (currentQuestion.views || 0) + 1 })
        .eq('id', questionId);

      if (updateError) {
        console.error('Error updating views:', updateError);
        throw updateError;
      }

      const viewUrl = `/question/view/${questionId}`;
      console.log('Navigating to:', viewUrl);
      navigate(viewUrl);
    } catch (error) {
      console.error('Error in handleViewQuestion:', error);
    }
  };

  // Add this function to fetch comments for a specific question
  const fetchComments = async (questionId) => {
    try {
      const { data, error } = await supabaseClient
        .from('comments')
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url
          )
        `)
        .eq('question_id', questionId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setComments(prev => ({ ...prev, [questionId]: data }));
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  // Add this function to handle comment submission
  const handleCommentSubmit = async (questionId) => {
    if (!user || !newComment[questionId]?.trim()) return;

    try {
      const { error } = await supabaseClient
        .from('comments')
        .insert({
          content: newComment[questionId],
          question_id: questionId,
          user_id: user.id
        });

      if (error) throw error;

      // Clear the comment input and refresh comments
      setNewComment(prev => ({ ...prev, [questionId]: '' }));
      fetchComments(questionId);
    } catch (error) {
      console.error('Error submitting comment:', error);
    }
  };

  // Add this function to toggle comments visibility
  const toggleComments = async (questionId) => {
    setOpenComments(prev => {
      const newState = { ...prev, [questionId]: !prev[questionId] };
      if (newState[questionId]) {
        fetchComments(questionId);
      }
      return newState;
    });
  };

  // Add this debug log in the render
  console.log('Rendering component with questions:', questions);

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link', 'code-block'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'link', 'code-block'
  ];

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh'
    }}>
      <Nav1 />
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4 
        }}>
          <Typography variant="h4">
            Recent Questions
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAskDialog}
            sx={{
              backgroundColor: '#108a00',
              '&:hover': {
                backgroundColor: '#0c6b00'
              }
            }}
          >
            Ask Question
          </Button>
        </Box>
        
        {/* Ask Question Dialog */}
        <Dialog 
          open={openAskDialog} 
          onClose={handleCloseAskDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Ask a Question</DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleQuestionSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Title"
                placeholder="Be specific and imagine you're asking a question to another person"
                value={newQuestion.title}
                onChange={(e) => setNewQuestion({ ...newQuestion, title: e.target.value })}
                required
                sx={{ mb: 3 }}
              />
              
              {/* Rich Text Editor */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                  Content *
                </Typography>
                <Box sx={{ 
                  '.ql-container': {
                    height: '200px',
                    fontSize: '1rem',
                    fontFamily: 'inherit'
                  },
                  '.ql-editor': {
                    minHeight: '200px'
                  },
                  '.ql-toolbar': {
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderBottom: 'none',
                    borderTopLeftRadius: '4px',
                    borderTopRightRadius: '4px',
                  },
                  '.ql-container': {
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                    borderTop: 'none',
                    borderBottomLeftRadius: '4px',
                    borderBottomRightRadius: '4px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                  },
                  '.ql-editor.ql-blank::before': {
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontStyle: 'normal'
                  }
                }}>
                  <ReactQuill 
                    value={newQuestion.content}
                    onChange={(content) => setNewQuestion({ ...newQuestion, content })}
                    modules={modules}
                    formats={formats}
                    placeholder="Include all the information someone would need to answer your question"
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Tags"
                placeholder="Add up to 5 tags to describe what your question is about"
                value={newQuestion.tags}
                onChange={(e) => setNewQuestion({ ...newQuestion, tags: e.target.value })}
                helperText="Separate tags with commas (e.g., windows, installation, network)"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseAskDialog} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleQuestionSubmit}
              variant="contained"
              sx={{
                backgroundColor: '#108a00',
                '&:hover': {
                  backgroundColor: '#0c6b00'
                }
              }}
            >
              Post Your Question
            </Button>
          </DialogActions>
        </Dialog>

        <Grid container spacing={3}>
          {/* Main content - Questions List */}
          <Grid item xs={12} md={8}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : questions.length === 0 ? (
              <Typography>No questions found</Typography>
            ) : (
              <>
                {questions.map((question) => (
                  <Card key={question.id} sx={{ mb: 2, p: 3 }}>
                    <Box sx={{ display: 'flex' }}>
                      {/* Voting buttons */}
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        mr: 2,
                        minWidth: '50px'
                      }}>
                        <IconButton 
                          size="small"
                          onClick={() => handleVote(question.id, 'up')}
                        >
                          <ArrowUpward />
                        </IconButton>
                        <Typography variant="h6" sx={{ my: 1 }}>
                          {question.votes || 0}
                        </Typography>
                        <IconButton 
                          size="small"
                          onClick={() => handleVote(question.id, 'down')}
                        >
                          <ArrowDownward />
                        </IconButton>
                      </Box>

                      {/* Question content */}
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ flex: 1 }}>
                            {question.title}
                          </Typography>
                          <IconButton 
                            onClick={() => handleViewQuestion(question.id)}
                            sx={{ 
                              color: 'primary.main',
                              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Box>
                        <Box 
                          sx={{ 
                            mb: 2,
                            maxHeight: '100px',
                            overflow: 'hidden',
                            position: 'relative',
                            '&::after': {
                              content: '""',
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '50px',
                              background: 'linear-gradient(transparent, white)'
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: question.content }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          {Array.isArray(question.tags) && question.tags.map((tag, index) => (
                            <Chip 
                              key={index} 
                              label={tag} 
                              size="small"
                              sx={{ 
                                backgroundColor: '#f0f7ff',
                                '&:hover': {
                                  backgroundColor: '#e3f2fd'
                                }
                              }}
                            />
                          ))}
                        </Box>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: 'text.secondary',
                          fontSize: '0.875rem'
                        }}>
                          <Typography variant="caption">
                            Posted {new Date(question.created_at).toLocaleDateString()}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                cursor: 'pointer' 
                              }}
                              onClick={() => toggleComments(question.id)}
                            >
                              <ChatBubbleOutline sx={{ fontSize: 18, mr: 0.5 }} />
                              {(comments[question.id]?.length || 0)}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Visibility sx={{ fontSize: 18, mr: 0.5 }} />
                              {question.views || 0}
                            </Box>
                          </Box>
                        </Box>

                        {/* Comments Section */}
                        <Collapse in={openComments[question.id]}>
                          <Box sx={{ mt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.12)', pt: 2 }}>
                            {/* Comments List */}
                            {comments[question.id]?.map((comment) => (
                              <Box 
                                key={comment.id} 
                                sx={{ 
                                  display: 'flex', 
                                  gap: 1, 
                                  mb: 1.5,
                                  alignItems: 'flex-start'
                                }}
                              >
                                <Avatar 
                                  src={comment.profiles?.avatar_url} 
                                  sx={{ width: 24, height: 24 }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>
                                      {comment.profiles?.username || 'Anonymous'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(comment.created_at).toLocaleDateString()}
                                    </Typography>
                                  </Box>
                                  <Typography variant="body2">
                                    {comment.content}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}

                            {/* Comment Input */}
                            {user && (
                              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                <TextField
                                  size="small"
                                  fullWidth
                                  placeholder="Add a comment..."
                                  value={newComment[question.id] || ''}
                                  onChange={(e) => setNewComment(prev => ({
                                    ...prev,
                                    [question.id]: e.target.value
                                  }))}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleCommentSubmit(question.id);
                                    }
                                  }}
                                />
                                <IconButton 
                                  onClick={() => handleCommentSubmit(question.id)}
                                  disabled={!newComment[question.id]?.trim()}
                                  sx={{ color: '#108a00' }}
                                >
                                  <Send />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    </Box>
                  </Card>
                ))}

                {/* Pagination */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 4,
                  mb: 2
                }}>
                  <Pagination 
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#108a00',
                      },
                      '& .Mui-selected': {
                        backgroundColor: '#108a00 !important',
                        color: 'white',
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Card sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Popular Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label="Windows (156)" size="small" />
                <Chip label="Hardware (98)" size="small" />
                <Chip label="Network (87)" size="small" />
                <Chip label="Software (76)" size="small" />
                <Chip label="Security (65)" size="small" />
              </Box>
            </Card>

            <Card sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Top Contributors
              </Typography>
              {/* Add top contributors list here */}
            </Card>
          </Grid>
        </Grid>
      </Container>
      <Footer />
    </Box>
  );
};

export default Question; 